import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { getCookie } from "../../utility/getCookie";
import {
  SegmentFilters,
  SegmentsResponse,
  SegmentState,
  SubSegment,
  SellerFullDetails,
  SellersWithDigitalProductsResponse,
  StoreFilter,
} from "./types";

const API_URL = [
  process.env.REACT_APP_API_BASE_URL,
  process.env.REACT_APP_SELLER_APP_BACKEND_PREFIX,
]
  .filter(Boolean)
  .join("");

if (!process.env.REACT_APP_API_BASE_URL) {
  console.warn("Warning: REACT_APP_API_BASE_URL is not defined.");
}
if (!process.env.REACT_APP_SELLER_APP_BACKEND_PREFIX) {
  console.warn("Warning: REACT_APP_SELLER_APP_BACKEND_PREFIX is not defined.");
}

// Axios instance with cookies support
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add Bearer token from cookies
api.interceptors.request.use(
  async (config) => {
    const token = getCookie("access_token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

const initialState: SegmentState = {
  segments: [],
  subSegments: [],
  subSegmentsMap: {},
  summary: null,
  totalRecords: 0,
  totalPages: 0,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
  },
  search: "",
  location: "",
  selectedSeller: null,
  sellersWithProducts: [],
  selectedSegment: "",
  selectedSubsegment: "",
  department: "",
};

// Async thunk for fetching all segments
export const getAllSegments = createAsyncThunk(
  "segment/getAllSegments",
  async (filters: SegmentFilters, { rejectWithValue }) => {
    try {
      const response = await api.get<SegmentsResponse>("/segments", {
        params: filters,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch segments",
      );
    }
  },
);

// Async thunk for fetching sub-segments by segment name
export const getSubSegmentsByName = createAsyncThunk(
  "segment/getSubSegmentsByName",
  async (segmentName: string, { rejectWithValue }) => {
    try {
      const response = await api.get<SubSegment[]>(
        `/segments/find-by-name`,
        { params: { segmentName } },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || `Failed to fetch sub-segments for ${segmentName}`,
      );
    }
  },
);

// Async thunk for fetching comprehensive seller details
export const getSellerFullDetails = createAsyncThunk(
  "segment/getSellerFullDetails",
  async (sellerId: string, { rejectWithValue }) => {
    try {
      const response = await api.get<SellerFullDetails>(
        `/public/store/seller/full-details/${sellerId}`,
      );
      console.log("response for getSellerFullDetails", response.data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || `Failed to fetch details for seller ${sellerId}`,
      );
    }
  },
);

// Async thunk for fetching stores with digital products
export const getSellersWithDigitalProducts = createAsyncThunk(
  "segment/getSellersWithDigitalProducts",
  async (filter: StoreFilter, { rejectWithValue }) => {
    try {
      const response = await api.get<SellersWithDigitalProductsResponse>(
        "/public/store/sellers-with-digital-products",
        { params: filter },
      );
      console.log("response for getSellersWithDigitalProducts", response.data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch stores with digital products",
      );
    }
  },
);

const segmentSlice = createSlice({
  name: "segment",
  initialState,
  reducers: {
    setPagination: (state, action: PayloadAction<{ page: number; limit: number }>) => {
      state.pagination = action.payload;
    },
    setSearch: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
    },
    setLocation: (state, action: PayloadAction<string>) => {
      state.location = action.payload;
    },
    clearSegmentError: (state) => {
      state.error = null;
    },
    clearSubSegments: (state) => {
      state.subSegments = [];
      state.subSegmentsMap = {};
    },
    clearSelectedSeller: (state) => {
      state.selectedSeller = null;
    },
    setSelectedSegment: (state, action: PayloadAction<string>) => {
      state.selectedSegment = action.payload;
    },
    setSelectedSubsegment: (state, action: PayloadAction<string>) => {
      state.selectedSubsegment = action.payload;
    },
    setDepartment: (state, action: PayloadAction<string>) => {
      state.department = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // getAllSegments
      .addCase(getAllSegments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllSegments.fulfilled, (state, action) => {
        state.loading = false;
        state.segments = action.payload.data;
        state.summary = action.payload.summary;
        state.totalRecords = action.payload.totalRecords;
        state.totalPages = action.payload.totalPages;
        state.pagination.page = action.payload.page;
        state.pagination.limit = action.payload.limit;
      })
      .addCase(getAllSegments.rejected, (state, action) => {
        state.loading = false;
      })
      // getSubSegmentsByName
      .addCase(getSubSegmentsByName.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSubSegmentsByName.fulfilled, (state, action) => {
        state.loading = false;
        state.subSegments = action.payload; // For backward compatibility
        const segmentName = action.meta.arg;
        state.subSegmentsMap[segmentName] = action.payload;
      })
      .addCase(getSubSegmentsByName.rejected, (state, action) => {
        state.loading = false;
      })
      // getSellerFullDetails
      .addCase(getSellerFullDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSellerFullDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedSeller = action.payload;
      })
      .addCase(getSellerFullDetails.rejected, (state, action) => {
        state.loading = false;
      })
      // getSellersWithDigitalProducts
      .addCase(getSellersWithDigitalProducts.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSellersWithDigitalProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.sellersWithProducts = action.payload.data;
        state.totalRecords = action.payload.meta.total;
        state.totalPages = action.payload.meta.totalPages;
        state.pagination.page = action.payload.meta.page;
        state.pagination.limit = action.payload.meta.limit;
      })
      .addCase(getSellersWithDigitalProducts.rejected, (state, action) => {
        state.loading = false;
      });
  },
});

export const {
  setPagination,
  setSearch,
  setLocation,
  clearSegmentError,
  clearSubSegments,
  clearSelectedSeller,
  setSelectedSegment,
  setSelectedSubsegment,
  setDepartment,
} = segmentSlice.actions;

export default segmentSlice.reducer;
